import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import usePrevious from 'hooks/usePrevious';
import currencyUtils from 'utils/currency';
import { orderPlacedMessage } from 'utils/orderConfirmation';
import TaggedInputText from 'components/UI/TaggedInputText';
import TextInput from 'components/UI/TextInput';
import Tooltip from 'components/UI/Tooltip';
import Spinner from 'components/UI/Spinner/Spinner';
import PaymentOptions from 'containers/Menu/Cart/PaymentOptions';
import PaymentButton from 'containers/Menu/Cart/PaymentButton';
import loginService from 'services/loginService';
import ModalSuccessPayment from 'components/UI/Modal/ModalSuccessPayment';
import ModalError from 'components/UI/Modal/ModalError';
import ModalReceiptVoucher from '../../../components/UI/Modal/ModalReceiptVoucher';
import ModalValidateEmails from 'components/UI/Modal/ModalValidateEmails';
import { remove } from 'actions/TaggedInputText';
import { createNewUserByEmail } from 'actions/SignUp';
import {
  placeOrderAndPay,
  closeModalError,
  placeOrderAndGoToAddNewCard,
} from 'actions/TeamVoucher';

import * as actions from 'actions/index';

const TeamVoucherComponent = ({
  cards,
  order,
  campaignRateCC,
  isLoading,
  teamVoucherList,
  onOpenModalConfirmEmails,
  onFetchingCards,
  cartResetState,
  listAddress,
  onTeamVoucherSetOrder,
  onPlaceOrderAndPay,
  handlePlaceOrderAndGoToAddNewCard,
  showModalPlaceOrder,
  history,
  onCloseModalPlacedOrderAndRedirect,
  onOpenModalReceipt,
  showModalReceipt,
  onCloseModalReceiptAndRedirect,
  userDetails,
  showModalError,
  onCloseModalError,
  errorMessage,
  errorType,
  errorObject,
  onInvalidTeamVoucherEmail,
  createNewUserByEmail,
  showModalConfirmEmails,
  onCloseModalConfirmEmails,
  onVerifyUnregisteredEmails,
  onFetchValidTeamVouchers,
  onFetchTeamVoucherCampaignRate,
}) => {
  const [vouchersEmail, setVouchersEmail] = useState([]);
  const [amountDisabled, setAmountDisabled] = useState(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [voucherUnitValue, setVoucherUnitValue] = useState(0);
  const [total, setTotal] = useState(0);
  const [payType, setPayType] = useState(null);

  const [cardSelected, setCardSelected] = useState(null);

  const [filteredUnregisteredEmails, setFilteredUnregisteredEmails] = useState(
    []
  );

  const previousVouchersEmail = usePrevious(vouchersEmail);

  useEffect(() => {
    onFetchingCards();
    onFetchValidTeamVouchers();
    onFetchTeamVoucherCampaignRate();
  }, []);

  useEffect(() => {
    if (teamVoucherList.length !== vouchersEmail.length) {
      setVouchersEmail(teamVoucherList);
      setTotal(voucherUnitValue * teamVoucherList.length);
    }
  }, [teamVoucherList, vouchersEmail]);

  useEffect(() => {
    if (previousVouchersEmail < vouchersEmail) {
      setAmountDisabled(false);
    }
  }, [vouchersEmail, previousVouchersEmail]);

  const isPositiveNumber = value => {
    return !!value && !!isFinite(value) && parseFloat(value) >= 0;
  };

  const handleValidateEmails = async () => {
    const emails = vouchersEmail;
    const emailsToFilter = emails.filter(email => !email.created);
    for (let i = 0; i < emailsToFilter.length; i++) {
      emailsToFilter[i].registered = await checkUserRegistered(
        emailsToFilter[i]
      );
    }
    let emailSet = [...new Set(emailsToFilter)];
    let uniqueEmails = emailSet.filter(function ({ label }) {
      return !this.has(label) && this.add(label);
    }, new Set());
    const unregisteredEmails = uniqueEmails.filter(
      email => email.registered === false
    );

    setFilteredUnregisteredEmails(unregisteredEmails);

    if (unregisteredEmails.length !== 0) {
      onOpenModalConfirmEmails();
    } else {
      setAmountDisabled(true);
    }
  };

  const checkUserRegistered = async email => {
    try {
      const response = await loginService.preLogin({ email: email.value });
      if (response.userAlreadyRegistered && response.hasRelatedBusiness) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const updateField = ({ target }) => {
    let newValue = '';
    let newTotal = total;
    switch (target.type) {
      case 'number':
        newValue = isPositiveNumber(target.value)
          ? parseFloat(target.value)
          : 1;
        newTotal = newValue * vouchersEmail.length;
        break;
      default:
        newValue = target.value;
        break;
    }
    setVoucherUnitValue(newValue);
    setTotal(newTotal);
  };

  const onHandleSelectPaymentMethod = (id, type) => {
    switch (type) {
      case "ideal":
        setPayType('I');
        setCardSelected(null);
        break;
      case "bancontact":
        setPayType('B');
        setCardSelected(null);
        break;
      case 'onCreditCard':
        if (id === 'payWithAnotherCard') {
          setPayType('C');
          setCardSelected(null);
        } else {
          const card = cards.find(item => item.cardId === id);
          setPayType('C');
          setCardSelected(card);
        }
        break;
      default:
        break;
    }
  };

  const getAddressForVoucher = () => {
    let address = listAddress.find(address => !!!address.forCollection);
    return address.idBusinessAddress;
  };

  const buildingOrder = () => {
    const orderItems = vouchersEmail.map(emailOption => {
      return {
        quantity: 1,
        product: {
          name: emailOption.value,
          price: voucherUnitValue,
        },
        total: voucherUnitValue,
      };
    });
    return {
      totalAmount: total,
      payType,
      orderItems,
      businessDeliveryAddress: { id: getAddressForVoucher() },
    };
  };

  const getTotalOrderValuePlusCampaignRateValue = () => {
    return campaignRateCC.amount + total;
  };

  const getPaymentReference = () => {
    return cardSelected ? cardSelected.cardId : null;
  };

  const getPayInfo = () => {
    return {
      amount: getTotalOrderValuePlusCampaignRateValue(),
      currency: 'EUR',
      paymentReference: getPaymentReference(),
      idBusinessRestaurant: getAddressForVoucher(),
      payObjectType: 'O',
      payType: payType,
    };
  };

  const onHandlePlaceOrder = () => {

    setIsButtonDisabled(true);
    cartResetState();
    const order = buildingOrder();

    onTeamVoucherSetOrder(order);
    if (payType === 'C' && !!cardSelected) {
      onPlaceOrderAndPay(order, getPayInfo());
    } else {
      handlePlaceOrderAndGoToAddNewCard(order, getPayInfo(), history);
    }
  };

  const getCartItensFromTeamVouchers = () => {
    if (!!order.orderItems) {
      return {
        idOrder: order.idOrder,
        products: order.orderItems.map(item => item.product),
      };
    }
    return {};
  };

  const getSurcharge = () => {
    return campaignRateCC.amount;
  };

  const getReceiptData = () => {
    return {
      cart: getCartItensFromTeamVouchers(),
      total: getTotalOrderValuePlusCampaignRateValue(),
      currency: currencyUtils.convertNameToSymbol('EUR'),
      surcharge: getSurcharge(),
      subtotal: total,
      userDetails,
      forCollection: false,
    };
  };

  const modalErrorClose = () => {
    if (errorType === 'invalid-team-voucher') {
      onInvalidTeamVoucherEmail(errorObject);
    } else if (errorType === 'not-accepted-invalid-team-voucher') {
      onInvalidTeamVoucherEmail(errorObject);
    } else {
      setIsButtonDisabled(false);
    }
    onCloseModalError();
  };

  const modalErrorActionButton = () => {
    if (errorType === 'invalid-team-voucher') {
      createNewUserByEmail(errorObject);
    } else if (errorType === 'not-accepted-invalid-team-voucher') {
      onInvalidTeamVoucherEmail(errorObject);
    } else {
      setIsButtonDisabled(false);
    }
    onCloseModalError();
  };

  const modalErrorActionButtonTitle = () => {
    if (errorType === 'invalid-team-voucher') {
      return 'Create User';
    } else {
      return 'Close';
    }
  };

  const createUnregisteredEmails = emails => {
    let createdEmails = [];
    let emailsToCreate = emails.filter(email => !email.created);
    for (let i = 0; i < emailsToCreate.length; i++) {
      try {
        emailsToCreate[i].created = true;
        const email = createNewUserByEmail(emailsToCreate[i]);
        createdEmails.push(email);
      } catch (err) {
        console.log(err.message);
      }
    }
    setAmountDisabled(true);
    setFilteredUnregisteredEmails(createdEmails);
    onCloseModalConfirmEmails();
  };

  const handleFilterUnregisterEmails = mail => {
    const filtered = filteredUnregisteredEmails.filter(
      email => email.label !== mail.label
    );
    onInvalidTeamVoucherEmail(mail);
    setFilteredUnregisteredEmails(filtered);
    setVouchersEmail(filtered);
    setAmountDisabled(true);
  };

  const tooltipText =
    'Vouchers will be sent to the email addresses provided.' +
    'Email addresses do not need to be company emails.' +
    'Any voucher money not spent will be refunded to your card.';
  return (
    <React.Fragment>
      <ModalSuccessPayment
        descriptionSuccess={orderPlacedMessage(order.publicOrderNumber)}
        isInvoice={false}
        showModal={showModalPlaceOrder}
        closeModal={() => onCloseModalPlacedOrderAndRedirect(history)}
        openReceipt={() => onOpenModalReceipt()}
      />
      <ModalReceiptVoucher
        showModal={showModalReceipt}
        closeModal={() => onCloseModalReceiptAndRedirect(history)}
        data={getReceiptData()}
      />
      <ModalError
        style={{ zIndex: '99999', position: 'absolute', margin: '5% 26%' }}
        styleContent={{ background: 'gainsboro' }}
        showModal={showModalError}
        closeModal={() => modalErrorClose()}
        actionButton={() => modalErrorActionButton()}
        actionButtonTitle={modalErrorActionButtonTitle()}
        errorMessage={errorMessage || 'A problem ocurred with your order'}
      />
      <ModalValidateEmails
        style={{ zIndex: '99999', position: 'absolute', margin: '5% 26%' }}
        styleContent={{ background: 'gainsboro' }}
        showModal={showModalConfirmEmails}
        closeModal={() => onCloseModalConfirmEmails()}
        actionButton={() =>
          createUnregisteredEmails(filteredUnregisteredEmails)
        }
        actionButtonTitle="CREATE USERS"
        unregistered={filteredUnregisteredEmails}
        filterAction={handleFilterUnregisterEmails}
        validateAction={onVerifyUnregisteredEmails}
      />
      {/* <div className="balance">
        <h3>Your Balance</h3>
        <h4>Voucher Amount: </h4>
        {!!availableTeamVouchers.length ? (
          <span>
            <b>{availableTeamVouchersTotal}</b> (Expires{' '}
            {timeUtils.formatDateToTimeAmPm(
              availableTeamVouchers[0].expiryDate
            )}
            )
          </span>
        ) : (
          <span>
            <b>0</b>
          </span>
        )}
      </div> */}
      <div className="order-voucher">
        <h4 className="fl">Order Vouchers</h4>
        <Tooltip
          className="fr"
          tooltipText={tooltipText}
          tooltipSpanText="How it Works"
        />
        <div className="clear flex-row clearfix">
          <div className="email fl">
            <label>Email(s)</label>
            {/* <TaggedInputText
            validate={this.props.onCustomerExist}
            value={this.props.vouchersEmail}
          /> */}
            <TaggedInputText />
            <button
              onClick={handleValidateEmails}
              className="btn orange-bold"
              type="button"
            >
              Validate
            </button>
          </div>
          <div className="voucher fr">
            <label>Amount of Voucher</label>
            {!amountDisabled ? (
              <p>Validate options first!</p>
            ) : (
              <TextInput
                name="voucherUnitValue"
                type="number"
                step=".01"
                placeholder=""
                onChange={updateField}
                value={voucherUnitValue}
                className="amount-voucher"
              />
            )}
          </div>
        </div>
        <div className="clear valor-final">
          <span className="quant-voucher fl">
            {vouchersEmail.length} Vouchers x €
            {voucherUnitValue !== null && voucherUnitValue.toFixed(2)}{' '}
          </span>
          <span className="total fr"> Total €{total} </span>
        </div>
      </div>
      <PaymentOptions
        disabled={isButtonDisabled}
        payTypeSelected={payType}
        cardSelected={cardSelected}
        selectPaymentMethod={onHandleSelectPaymentMethod}
        cards={cards}
      />
      {isLoading ? <Spinner nameClass={"lds-third"}/> : (
        <PaymentButton
          payTypeSelected={payType}
          handlePlaceOrder={onHandlePlaceOrder}
          containerClassName="tools"
          buttonText="Purchase Vouchers Now"
          campaignRateCC={campaignRateCC}
          disabled={isButtonDisabled || vouchersEmail.length < 1 || total <= 0}
        />
      )}

      <h5 className="note">
        <strong>Note: </strong>Vouchers currently expire at 12pm the following
        morning.
      </h5>
    </React.Fragment>
  );
};

const mapDispatchToProps = dispatch => ({
  onOpenModalConfirmEmails: () => dispatch(actions.openModalConfirmEmails()),
  onCloseModalConfirmEmails: () => dispatch(actions.closeModalConfirmEmails()),
  onFetchingCards: () => dispatch(actions.teamVoucherFetchingCards()),
  onFetchValidTeamVouchers: () => dispatch(actions.fetchValidTeamVouchers()),
  cartResetState: () => dispatch(actions.cartResetState()),
  onTeamVoucherSetOrder: order => dispatch(actions.teamVoucherSetOrder(order)),
  onPlaceOrderAndPay: (order, payInfo) =>
    dispatch(placeOrderAndPay(order, payInfo)),
  handlePlaceOrderAndGoToAddNewCard: (order, payInfo, history) =>
    dispatch(placeOrderAndGoToAddNewCard(order, payInfo, history)),
  onCloseModalPlacedOrderAndRedirect: history =>
    dispatch(actions.closeModalPlacedOrderAndRedirect(history)),
  onOpenModalReceipt: () => dispatch(actions.openModalReceipt()),
  onCloseModalReceiptAndRedirect: history =>
    dispatch(actions.closeModalReceiptAndRedirect(history)),
  onCloseModalError: () => dispatch(closeModalError()),
  onInvalidTeamVoucherEmail: option => dispatch(remove(option)),
  createNewUserByEmail: option => dispatch(createNewUserByEmail(option.value)),
  onVerifyUnregisteredEmails: (emailList, newEmail) =>
    dispatch(actions.verifyUnregisteredEmails(emailList, newEmail)),
  onFetchTeamVoucherCampaignRate: () =>
    dispatch(actions.fetchTeamVoucherCampaignRate()),
});

const mapStateToProps = state => ({
  order: state.teamVoucherReducer.order,
  isLoading: state.loadingReducer.loading,
  showModalError: state.teamVoucherReducer.showModalError,
  showModalPlaceOrder: state.teamVoucherReducer.showModalPlaceOrder,
  showModalReceipt: state.teamVoucherReducer.showModalReceipt,
  showModalConfirmEmails: state.teamVoucherReducer.showModalConfirmEmails,
  errorMessage: state.teamVoucherReducer.errorMessage,
  errorType: state.teamVoucherReducer.errorType,
  errorObject: state.teamVoucherReducer.errorObject,
  listAddress: state.menuReducer.listAddress,
  userDetails: state.authenticationReducer.userDetails,
  teamVoucherList: state.taggedInputTextReducer.values,
  cards: state.teamVoucherReducer.cards,
  campaignRateCC: state.teamVoucherReducer.campaignRateCC,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TeamVoucherComponent)
);



// WEBPACK FOOTER //
// ./src/containers/TeamVoucher/TeamVoucherComponent/index.js